import React, { useLayoutEffect, useRef, useState } from "react";
import * as SRE from "speech-rule-engine";

// Whoever uses this component MUST ensure they are loading MathJax somehow.
declare global {
  interface Window {
    __MathJax_State__?: {
      isReady: boolean;
      promise: Promise<void>;
    };
    MathJax?: any;
  }
}

type MathProps = (
  | {
      tex?: string;
      mathml?: never;
    }
  | {
      tex?: never;
      mathml?: string;
    }
) & {
  display?: boolean;
  language?: string;
};

const Math = ({ mathml, tex, display = false, language = "en" }: MathProps) => {
  const rootElementRef: React.RefObject<HTMLSpanElement> = useRef(null);

  const __MathJax_State__ = window["__MathJax_State__"];

  // Store this in local state so we can make the component re-render when
  // it's updated.
  const [isReady, setIsReady] = useState(__MathJax_State__?.isReady);

  // Generate speech text from TeX or MathML for accessibility
  const generateSpeechText = (
    texSource?: string,
    mathmlSource?: string,
    speechLanguage: string = "en",
  ): string | null => {
    try {
      // Map language codes to SRE locale format
      const getSreLocale = (lang: string): string => {
        const langCode = lang.toLowerCase();
        // See packages/question-player/src/utils/languageResolution.ts for supported languages
        const localeMap: Record<string, string> = {
          en: "en",
          es: "es",
          fr: "fr",
          de: "de",
        };
        return localeMap[langCode] || "en";
      };

      const sreLocale = getSreLocale(speechLanguage);

      // Setup speech-rule-engine with language support
      // Note: SRE currently uses domain/style settings primarily for different speech styles
      // Language-specific localization is handled by the TTS engine itself
      SRE.setupEngine({
        domain: "mathspeak",
        style: "default",
        locale: sreLocale,
      });

      if (mathmlSource) {
        // Use MathML directly if available
        return SRE.toSpeech(mathmlSource);
      } else if (texSource) {
        // For TeX, we need to convert it to MathML first using MathJax
        // This is a simplified approach - in practice, we might need to handle this asynchronously
        try {
          // Try to generate speech from TeX if possible
          return SRE.toSpeech(`<math><mtext>${texSource}</mtext></math>`);
        } catch (e) {
          // If direct conversion fails, return a fallback
          return `Math expression: ${texSource}`;
        }
      }
    } catch (e) {
      console.warn("Speech generation failed:", e);
    }
    return null;
  };

  const speechText = generateSpeechText(tex, mathml, language);

  useLayoutEffect(() => {
    // Avoid running this script if the MathJax library hasn't loaded yet.
    if (!isReady) {
      // But trigger a re-render of this component once it is loaded.
      __MathJax_State__?.promise.then(() => setIsReady(true));
      return;
    }

    if (!rootElementRef.current) {
      return;
    }

    const MathJax = window["MathJax"];

    // This element won't be null at this point.
    const mathElement = rootElementRef.current;

    // Remove previous typeset children.
    mathElement.innerHTML = "";

    // Reset equation numbers.
    MathJax.texReset();

    // Construct options.
    const options = MathJax.getMetricsFor(mathElement);
    options.display = display;

    // Potentially this could/should be switched to use the synchronous version
    // of this MathJax function, `MathJax.tex2svg()`.
    if (tex) {
      MathJax.tex2svgPromise(tex, options)
        .then((node: HTMLElement) => {
          if (speechText) {
            node.setAttribute("alt", speechText);
            node.setAttribute("aria-label", speechText);
          }

          // `mathElement.replaceWith(node)` would be nicer markup-wise, but that
          // seems to mess with React's diffing logic.
          mathElement.appendChild(node);
          // Update the global MathJax CSS to account for this new element.
          MathJax.startup.document.clear();
          MathJax.startup.document.updateDocument();
        })
        .catch((err: any) => {
          console.error(err);
        });
    } else if (mathml) {
      // NOTE: `display` option is *not* respected for mathml
      // const converted = mathml;
      const converted = mathml.replace(/<math/, `<math display="block"`);
      MathJax.mathml2svgPromise(converted, options)
        .then((node: HTMLElement) => {
          if (speechText) {
            node.setAttribute("alt", speechText);
            node.setAttribute("aria-label", speechText);
          }

          mathElement.appendChild(node);
          MathJax.startup.document.clear();
          MathJax.startup.document.updateDocument();
        })
        .catch((err: any) => {
          console.error(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tex, display, isReady, mathml]);

  if (!__MathJax_State__) {
    console.error("You did not initialize the MathJax library");
    return null;
  }

  return (
    <span
      ref={rootElementRef}
      data-mathml={mathml}
      aria-label={speechText || undefined}
      title={speechText || undefined}
    />
  );
};

export default Math;
